import { useEffect, type Dispatch, type SetStateAction } from "react";
/** Keep keyboard navigation in an open dialog and restore the trigger on close. */
export function useDialogKeyboard(
  open: boolean,
  setOpen: Dispatch<SetStateAction<boolean>>,
  label: string,
) {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = () =>
      Array.from(
        document.querySelectorAll<HTMLElement>('[role="dialog"]'),
      ).find((el) => el.getAttribute("aria-label") === label);
    const focusables = () =>
      Array.from(
        dialog()?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
        ) ?? [],
      ).filter((el) => el.getClientRects().length > 0);
    const frame = requestAnimationFrame(() => focusables()[0]?.focus());
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
      if (event.key === "Tab") {
        const elements = focusables(),
          first = elements[0],
          last = elements.at(-1);
        if (!first) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handle);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handle);
      if (previous?.isConnected) previous.focus();
    };
  }, [open, setOpen, label]);
}
